import { defineStore } from "pinia";

import _ from 'lodash'
import Swal from 'sweetalert2'
import JobBuilder from "~/class/JobBuilderV1";
import { JobAction, JobMode } from "~/interface/Job";
import OrderBuilder from "~/class/OrderBuilder";
import { child, onValue, set } from "firebase/database";
import { PLAN, hasPlan } from "~/constants/packagePlan";
export const usePosCartStore = defineStore("cart", {
    state: () => ({
        tableName: "",
        tableNickname : "",
        isOpeningTable: false,
        mode: 1,
        timestamp: useServerTime().nowISO(),
        isSending: false,
        deliveryInfo: null,
        guests: 0,
        // Only packages with the kitchen-print feature (>= 1390) can flag orders
        // to the kitchen; lower tiers must always send toKitchen: false.
        toKitchen: hasPlan(PLAN.KITCHEN_PRINT_MIN),

        terminalTimeout: null as ReturnType<typeof setTimeout> | null,
        terminalUnsubscribe: null as (() => void) | null,
    }),

    getters: {
        itemCount: (state) => {
            return 1
        },
        getTogoCurrentQueueNumber: (state) => {
            return () => {
                const jobList = _.values(usePosJobsStore().jobs)
                const togoJobs = jobList.filter(job => job.mode === JobMode.TOGO)
                const alias = togoJobs.map(job => job.alias)
                const uniqueTogoTablesName = _.uniq(alias)
                return uniqueTogoTablesName.length
            }
        },
        getJobCurrentQueueNumber: (state) => {
            return () => {
                const jobList = _.values(usePosJobsStore().jobs)
                const jobs = jobList.filter(job => job.mode === JobMode.JOB)
                const alias = jobs.map(job => job.alias)
                const uniqueJobsTablesName = _.uniq(alias)
                return uniqueJobsTablesName.length
            }
        },
    },

    actions: {
        addTableRequest(table: any) {

            // const jobStoreV2 = useJobV2Store()
            this.tableName =  table.name
            this.tableNickname = table.nickName || null
            this.mode = resolveCartMode(table);
            this.timestamp = useServerTime().nowISO()
            // this.alias = table.alias || null
            this.isSending = false
            this.deliveryInfo = null
            this.toKitchen = hasPlan(PLAN.KITCHEN_PRINT_MIN)
            this.guests = 0

            if (table?.items?.length > 0) {
                this.isOpeningTable = false
            } else {
                this.isOpeningTable = true
            }




            this.onClearCart()
        },


        setGuestAmount(guestAmount) {
            this.guests = guestAmount
        },

        setDeliveryInfo(deliveryInfo) {
            this.deliveryInfo = deliveryInfo
        },


        async saveFromCashier(items, options: { note: string, data: any, type: string }) {

            if (!items || items.length === 0) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'No items',
                    text: 'Please add at least one item before saving.',
                });
                return;
            }

            if (this.isSending) return;
            this.isSending = true;
            try {
            const order = this._buildOrder(items, options);
            const job = this._buildJob(order, options);
            console.log(job)

            const jobsStore = usePosJobsStore();

            // togoNoService (SC=0 สำหรับ TO-GO) ถูกบังคับตอนคิดบิลใน calculateBilling แล้ว
            // ไม่ต้องเขียน sc:[0,0] ลง tableState ที่นี่อีก

            await jobsStore.add(job, true);
            // return the built job so the caller can select the newly-created
            // instance (job.alias is its table key in jobsStore.tables)
            return job;
            } finally {
                this.isSending = false;
            }
        },

        async saveFromTerminal(items, options: { note: string, data: any, type: string, source: string }, onSuccess, onError) {

            if (!items || items.length === 0) {
                await Swal.fire({
                    icon: 'warning',
                    title: 'No items',
                    text: 'Please add at least one item before saving.',
                });
                return false;
            }

            if (this.isSending) return false;
            this.isSending = true;

            // clean up any leftover handshake from a prior call
            if (this.terminalTimeout) { clearTimeout(this.terminalTimeout); this.terminalTimeout = null; }
            this.terminalUnsubscribe?.();
            this.terminalUnsubscribe = null;

            const order = this._buildOrder(items, options);
            const job = this._buildJob(order, options);
            console.log(job)

            const hash = job.hash?.toString() || ""
            const terminalRef = useRealtimeDatabase().handshakeTerminalRef()
            const childRef = child(terminalRef, hash)

            set(childRef, 1)

            const release = () => {
                if (this.terminalTimeout) { clearTimeout(this.terminalTimeout); this.terminalTimeout = null; }
                this.terminalUnsubscribe?.();
                this.terminalUnsubscribe = null;
                this.isSending = false;
            }

            this.terminalTimeout = setTimeout(() => {
                console.log('Timeout: no response after 7 seconds, unsubscribing')
                release()
                onError?.()
            }, 7000)

            this.terminalUnsubscribe = onValue(childRef, (snapshot) => {
                console.log(snapshot.val())
                if (snapshot.val() === 2) {
                    // send to cloud (unchanged legacy contract)
                    const terminalJobRef = useRealtimeDatabase().terminalJobRef()
                    const jobChildRef = child(terminalJobRef, hash)
                    set(jobChildRef, job)

                    release()

                    // ONLY a JOB that shipped without a nickName gets a number back
                    // from the cashier — that is the single case where waiting buys
                    // us anything. TABLE has no nickName at all and TOGO/DELIVERY
                    // already carry their own, so for those the result node never
                    // yields a name and waiting just burned the full guard timeout
                    // (~8s of "saving…" on every Add To Table). Finish immediately.
                    const awaitsNumber = job.mode === JobMode.JOB && !job.nickName
                    if (!awaitsNumber) {
                        onSuccess?.(job)
                        return
                    }

                    // Keep the terminal on the product screen ("saving…") until the
                    // cashier publishes the assigned running number, THEN fire
                    // onSuccess. This way the screen transition (back to the table
                    // list) and the "Job #N" toast happen TOGETHER — the toast no
                    // longer races ahead while the UI is still saving. Stamp the
                    // number onto the job so the caller shows it at the exact
                    // transition moment. Falls through with no number after a short
                    // guard so a slow/offline cashier never leaves the UI stuck.
                    this._awaitTerminalJobNumber(hash, (nickName) => {
                        if (nickName) job.nickName = nickName
                        onSuccess?.(job)
                    })
                }
            })

        },

        // Resolve the cashier-assigned running number from the additive
        // terminal_job_result/{hash} node, then call done(nickName). done(null) after
        // a short guard so the flow never hangs when no number comes back. Uses its
        // own listener/timer, independent of the handshake subscription above.
        _awaitTerminalJobNumber(hash: string, done: (nickName: string | null) => void) {
            if (!hash) { done(null); return }
            const resultRef = child(useRealtimeDatabase().terminalJobResultRef(), hash)
            let unsub: (() => void) | null = null
            let guard: ReturnType<typeof setTimeout> | null = null
            let settled = false
            const finish = (nickName: string | null) => {
                if (settled) return
                settled = true
                unsub?.(); unsub = null
                if (guard) { clearTimeout(guard); guard = null }
                done(nickName)
            }
            guard = setTimeout(() => finish(null), 8000)
            unsub = onValue(resultRef, (snap) => {
                const val = snap.val()
                // Any payload on this node means the cashier already handled the
                // order — settle on it. Waiting specifically for `nickName` used to
                // hang for the full guard whenever the cashier wrote it as null
                // (Firebase drops null keys, so the field arrives ABSENT, not empty).
                if (!val) return
                finish(val.nickName || null)
            })
        },

        _buildJob(order: Order, options = { note: "" }) {

            const jobStore = usePosJobsStore();
            const employeeStore = usePosEmployeesStore();
            const shopStore = usePosShopStore();

            const _hash = Math.random().toString(36).substring(2, 15)
            let table = this.tableName || _hash
            const note = options.note || "";
            const isOpeningTable = this.isOpeningTable

            const jobBuilder = new JobBuilder()
                .withActionType(JobAction.ADD)
                .withEmployee(employeeStore.getCurrentEmployee)
                .withPrint(this.toKitchen)
                .withOrderJson(order)
                .withJobIdAndTimestamp(this.timestamp)
                // Bill-level comment → kitchen slip. Mirrors AdapterMobileV1: note
                // feeds PrintJobNiceloopV1Builder's `Comments` field; comments on the
                // job keeps parity with the mobile/terminal-receive shape. Empty
                // strings are stripped by JobBuilder.build()'s omitBy.
                .withNote(note)

                .withCustom({
                    hash: _hash,
                    comments: note || null,
                });

            if(options?.source) { 
                jobBuilder.withSource(options.source);
            }

            switch (this.mode) {
                case JobMode.TABLE:
                    jobBuilder
                        .withGuests(this.guests)
                        .withMode(JobMode.TABLE)
                        .withIsOpeningTable(isOpeningTable)
                        .withTableAlias(table)
                        .withTableUuid(table);

                    break;
                case JobMode.TOGO:
                    let queue1 = this.tableNickname || ''
                    if (this.isOpeningTable && !queue1) {
                        queue1 = "Queue #" + (this.getTogoCurrentQueueNumber() + 1).toString()
                    }
                    jobBuilder
                        .withGuests(1)
                        .withMode(JobMode.TOGO)
                        .withTableAlias(table)
                        .withTableUuid(table)
                        .withNickName(queue1)
                    break;
                case JobMode.JOB:
                    let nickName = this.tableNickname || ''
                    // Terminal orders (source 't') must NOT self-number: 4 terminals
                    // each read their own lagging jobchain and would hand out the
                    // same "Job #N". Ship nickName empty (build()'s omitBy strips the
                    // empty string, so the field is ABSENT) and let the single cashier
                    // be the sole authority — it assigns the number on receipt in
                    // pos.terminal.ts watchOrderFromTerminal. Cashier-origin JOBs
                    // (saveFromCashier) keep numbering here: they build+add on the
                    // cashier and now read a fresh count via the optimistic insert.
                    if (this.isOpeningTable && !nickName && options?.source !== 't') {
                        nickName = "Job #" + (this.getJobCurrentQueueNumber() + 1).toString()
                    }
                    jobBuilder
                        .withGuests(1)
                        .withMode(JobMode.JOB)
                        .withTableAlias(table)
                        .withTableUuid(table)
                        .withNickName(nickName)
                    break;
                case JobMode.DELIVERY:

                    let nickName2 = this.tableNickname || ("K." + (this.deliveryInfo?.name || ""))
                    jobBuilder
                        .withGuests(1)
                        .withMode(JobMode.DELIVERY)
                        .withTableAlias(table)
                        .withTableUuid(table)
                        .withNickName(nickName2)
                        .withDeliveryInfo(this.deliveryInfo)

                    break;
            }

            return jobBuilder.build();
        },

        _buildOrder(items, options = { note: "" }) {
            // Combo menu: expand each combo parent into its "(W) " child lines
            // (price 0) HERE in the cart, before the job is built — so the SAME
            // job.json feeds BOTH the kitchen print and the table replay. (The
            // print plugin reads the job passed to jobsStore.add via $onAction
            // args, so expanding inside add() would miss the printer.) Covers both
            // saveFromCashier and saveFromTerminal — both funnel through here.
            // Idempotent — see expandItems.
            // NOTE: the item-level default discount is applied at ADD-TO-CART time
            // (handleAddItemToCart / quickAddItems set `discountrequest` on the
            // line) so the cashier SEES it in the cart before saving. We do NOT
            // stamp it here at build time — an invisible last-second mutation was
            // hard to verify. Lines already carry `discountrequest`; OrderBuilder
            // forwards it into the job chain.
            const expanded = usePosProductComboSetStore().expandItems(items)
            const orderBuilder = new OrderBuilder()
            orderBuilder
                .withOrderItems(expanded)
                .withNoPrint(!this.toKitchen)
                .withUuid(this.tableName)
                .withUniqueId(crypto.randomUUID())
                .withAlias(this.tableName)
            return orderBuilder.build()
        },


        onClearCart() {
            const cartItemStore = usePosCartItemsStore()
            cartItemStore.clearCart()
        },

        quickAddItems(table: any, items: any[]) {
            if (!items?.length) return

            // JOB is a one-shot order — never quick-add items to an existing JOB.
            if (resolveCartMode(table) === JobMode.JOB) return

            this.addTableRequest(table)

            const cartItemStore = usePosCartItemsStore()
            const productsStore = usePosProductsStore()
            const subitems = (productsStore as any).subitems || []
            const findOriginalPrice = (subItemId: any): number | null => {
                if (subItemId == null) return null
                const found = subitems.find((s: any) => String(s?.Id) === String(subItemId))
                const p = found?.Price
                return typeof p === 'number' ? p : (p != null ? Number(p) : null)
            }

            // Quick-add bypasses handleAddItemToCart, so stamp the item-level
            // default discount here too — via the same shared mapper — so the
            // re-added lines show their discount in the cart just like a normal add.
            const prepared = items.map((line) => ({
                ...line,
                quantity: 1,
                price: findOriginalPrice(line.subItemId) ?? line.price,
                toppings: (line.toppings || []).map((t: any) => ({ ...t })),
                comments: line.comments || '',
                dc1: undefined,
            }))
            const withDiscount = productsStore.applyDefaultDiscount(prepared)
            for (const line of withDiscount) {
                cartItemStore.addItemToCart(line)
            }

            const tabTypeByMode: Record<number, string> = {
                [JobMode.TOGO]: 'togo',
                [JobMode.JOB]: 'job',
                [JobMode.DELIVERY]: 'delivery',
                [JobMode.TABLE]: 'table',
            }
            useTabs().setTab('cart', (tabTypeByMode[this.mode] || 'table') as any)
        },

    },
});

function resolveCartMode(table) {
    if (table?.mode) {
        return table.mode
    }

    switch (table?.type) {
        case "delivery":
            return JobMode.DELIVERY
        case "togo":
            return JobMode.TOGO
        case "job":
            return JobMode.JOB
        case "table":
        default:
            return JobMode.TABLE
    }
}
